import { Observable } from 'rxjs';
import _ from 'lodash';
import { replace } from 'react-router-redux';

import { selectDeedById } from 'entities/deed/selectors';
import { selectCauses } from 'entities/cause/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectUserLocations } from 'entities/user/selectors';
import { selectVolunteerTimesByDeed } from 'entities/volunteerTime/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import CauseApi from 'entities/cause/api';
import CampaignApi from 'entities/campaign/api';
import DeedsApi from 'entities/deed/api';
import SkillsApi from 'entities/skill/api';
import LocationApi from 'entities/location/api';
import VolunteerTimeApi from 'entities/volunteerTime/api';
import { addAction, updateDeedPartiallyAction } from 'entities/deed/actions';
import Deed from 'entities/deed/model';

import {
  initSuccessAction,
  initFailedAction,
  cancelSuccessAction,
  cancelFailedAction,
  checkInAttendeeSuccessAction,
  checkInAttendeeFailedAction,
  removeAttendeeSuccessAction,
  removeAttendeeFailedAction,
  removeInviteeSuccessAction,
  removeInviteeFailedAction,
  setUserStatusSuccessAction,
  setUserStatusFailedAction,
  completeMilestoneSuccessAction,
  completeMilestoneFailedAction,
  respondVolunteerTimeSuccess,
  respondVolunteerTimeFailed,
  setProjectStartedSuccessAction,
  setProjectStartedFailedAction,
  setOptInStatusSuccessAction,
  setOptInStatusFailedAction,
} from './actions';
import {
  INIT,
  CANCEL,
  CHECK_IN_ATTENDEE,
  REMOVE_ATTENDEE,
  REMOVE_INVITEE,
  SET_USER_ROLE_STATUS,
  COMPLETE_MILESTONE,
  GET_VOLUNTEER_TIMES,
  APPROVE_VOLUNTEER_TIME,
  DENY_VOLUNTEER_TIME,
  SET_PROJECT_STARTED,
  SET_OPT_IN_STATUS,
} from './constants';

function loadDeedWithExternalLinkFollows(id) {
  const deedInfoObservable = Observable.from(DeedsApi.fetchInfo(id));
  const deedExternalLinkFollowsObservable = Observable.from(DeedsApi.fetchExternalLinkFollows(id));

  // Start both observables but delay processing
  const combinedObservables = Observable.forkJoin(
    deedInfoObservable,
    deedExternalLinkFollowsObservable
  );

  return Observable.merge(
    // Combined response from deedInfo and deedExternalLinkFollows are processed
    combinedObservables.map(([deedInfoResp, deedExternalLinkFollowsResp]) => [
      addAction(
        new Deed({
          ...deedInfoResp,
          ...deedExternalLinkFollowsResp,
        })
      ),
    ])
  );
}

const init = (action$, store) =>
  action$.ofType(INIT).mergeMap(({ id }) => {
    const actions = [];
    const state = store.getState();

    const causes = selectCauses(state);
    if (causes.size === 0) {
      actions.push(CauseApi.fetchAll());
    }

    const campaigns = selectCampaigns(state);
    if (campaigns.size === 0) {
      actions.push(CampaignApi.fetchAll());
    }

    const skills = selectSkills(state);
    if (skills.size === 0) {
      actions.push(SkillsApi.fetchAll());
    }

    const deed = selectDeedById(state, id);
    if (!deed) {
      actions.push(loadDeedWithExternalLinkFollows(id));
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationApi.fetchAll());
    }

    if (actions.length === 0) {
      return Observable.of(initSuccessAction());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const getVolunteerTimes = (action$, store) =>
  action$.ofType(GET_VOLUNTEER_TIMES).exhaustMap(({ id }) => {
    const state = store.getState();
    const volunteerTimes = selectVolunteerTimesByDeed(state, id);
    if (volunteerTimes.size) {
      return [];
    }
    return VolunteerTimeApi.getByFilter({ deedId: { equals: id } });
  });

const cancel = (action$) =>
  action$.ofType(CANCEL).exhaustMap(({ deed }) =>
    DeedsApi.delete(deed.id)
      .mergeMap((resultingAction) => [
        resultingAction,
        replace(`/deeds/${deed.pathSlug}`),
        showMessageAction(`Deed ${deed.manual ? 'removed' : 'cancelled'}!`),
        cancelSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(
          cancelFailedAction(e),
          showErrorAction(`${deed.manual ? 'Removal' : 'Cancellation'} failed!`)
        )
      )
  );

const checkInAttendee = (action$) =>
  action$.ofType(CHECK_IN_ATTENDEE).exhaustMap(({ deedId, userId, existingCheckIns }) =>
    DeedsApi.checkInAttendee(deedId, userId)
      .mergeMap((resp) => {
        if (!resp.success) {
          throw new Error('error checking in attendee');
        }

        return Observable.of(
          updateDeedPartiallyAction({
            id: deedId,
            checkIns: [...existingCheckIns, userId],
          })
        ).concat(
          // @NOTE-DP: re-fetch VT entries for the checked in user
          VolunteerTimeApi.getByFilter({
            deedId: { equals: deedId },
            userId: { equals: userId },
          })
        );
      })
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction('Checked in attendee!'),
        checkInAttendeeSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(checkInAttendeeFailedAction(e), showErrorAction('Check in attendee failed!'))
      )
  );

const completeMilestone = (action$) =>
  action$.ofType(COMPLETE_MILESTONE).exhaustMap(({ deedId, milestoneId }) =>
    DeedsApi.completeMilestone(deedId, milestoneId)
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction('Milestone completed!'),
        completeMilestoneSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(
          completeMilestoneFailedAction(e),
          showErrorAction('Milestone completion failed!')
        )
      )
  );

const removeInvitee = (action$) =>
  action$.ofType(REMOVE_INVITEE).exhaustMap(({ deedId, userId }) =>
    DeedsApi.removeInvitee(deedId, userId)
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction('Invitee Removed!'),
        removeInviteeSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(
          removeInviteeFailedAction(e),
          showErrorAction('Remove invitee failed! Please reload the page and try again.')
        )
      )
  );

const removeAttendee = (action$) =>
  action$.ofType(REMOVE_ATTENDEE).exhaustMap(({ deedId, userId, roleIds }) =>
    DeedsApi.removeAttendee(deedId, userId, roleIds)
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction('Attendee removed!'),
        removeAttendeeSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(
          removeAttendeeFailedAction(e),
          showErrorAction('Removal of attendee failed! Please reload the page and try again.')
        )
      )
  );

const setUserRoleStatus = (action$) =>
  action$.ofType(SET_USER_ROLE_STATUS).mergeMap(({ deedId, userId, state }) =>
    DeedsApi.setUserRoleStatus(deedId, userId, state)
      .mergeMap((resultingAction) => [resultingAction, setUserStatusSuccessAction(userId)])
      .catch((e) => Observable.of(setUserStatusFailedAction(e, userId)))
  );

const approveVolunteerTime = (action$) =>
  action$.ofType(APPROVE_VOLUNTEER_TIME).exhaustMap(({ id }) =>
    VolunteerTimeApi.approve(id)
      .mergeMap((resultingAction) => [
        resultingAction,
        respondVolunteerTimeSuccess(id),
        showMessageAction('Approved!'),
      ])
      .catch((error) =>
        Observable.of(respondVolunteerTimeFailed(id, error), showErrorAction('Approving Failed!'))
      )
  );

const denyVolunteerTime = (action$) =>
  action$.ofType(DENY_VOLUNTEER_TIME).exhaustMap(({ id }) =>
    VolunteerTimeApi.deny(id)
      .mergeMap((resultingAction) => [
        resultingAction,
        respondVolunteerTimeSuccess(id),
        showMessageAction('Denied!'),
      ])
      .catch((error) =>
        Observable.of(respondVolunteerTimeFailed(id, error), showErrorAction('Denying Failed!'))
      )
  );

const setProjectStarted = (action$) =>
  action$.ofType(SET_PROJECT_STARTED).exhaustMap(({ deed, user }) =>
    DeedsApi.update(deed.id, {
      startingAt: new Date(),
      [user.organization.type === 'Company' ? 'partner' : 'organization']: user.organization.id,
    })
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction(`Project started!`),
        setProjectStartedSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(setProjectStartedFailedAction(e), showErrorAction(`Project start failed!`))
      )
  );

const setOptInStatus = (action$) =>
  action$.ofType(SET_OPT_IN_STATUS).exhaustMap(({ deed, user, optIn }) =>
    DeedsApi.update(deed.id, {
      optedInCompanies: optIn ? [user.organization.id] : [],
    })
      .mergeMap((resultingAction) => [
        resultingAction,
        showMessageAction(`Deed successfully ${optIn ? 'added' : 'removed'}!`),
        setOptInStatusSuccessAction(),
      ])
      .catch((e) =>
        Observable.of(
          setOptInStatusFailedAction(e),
          showErrorAction('An error occurred. Please try again later.')
        )
      )
  );

export default [
  init,
  cancel,
  checkInAttendee,
  removeAttendee,
  removeInvitee,
  setUserRoleStatus,
  completeMilestone,
  getVolunteerTimes,
  approveVolunteerTime,
  denyVolunteerTime,
  setProjectStarted,
  setOptInStatus,
];
